import { useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Alert,
} from "@mui/material";
import { Controller, useForm, useWatch } from "react-hook-form";

interface Props {
  open: boolean;
  partName: string | null;
  currentPartialValue: number | null;
  /** When true, shows a checkbox to optionally apply to all sub-parts */
  showApplyToChildren?: boolean;
  /** When true, forces applyToChildren checked and disabled (non-replaceable containers) */
  forceApplyToChildren?: boolean;
  isLoading: boolean;
  onClose: () => void;
  onConfirm: (
    newPartialValue: number,
    operatorNumber: number,
    applyToChildren: boolean,
  ) => void;
}

interface FormData {
  newPartialValue: number;
  operatorNumber: string;
  applyToChildren: boolean;
}

const integerValidationRules = {
  required: "This field is required",
  validate: {
    isInteger: (value: string | number) =>
      Number.isInteger(Number(value)) || "Must be a whole number",
    isPositive: (value: string | number) =>
      Number(value) >= 0 || "Must be a positive number",
  },
};

export const SetInitialMetricModal = ({
  open,
  partName,
  currentPartialValue,
  showApplyToChildren = false,
  forceApplyToChildren = false,
  isLoading,
  onClose,
  onConfirm,
}: Props) => {
  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid },
  } = useForm<FormData>({
    mode: "onChange",
    defaultValues: {
      newPartialValue: 0,
      operatorNumber: "",
      applyToChildren: false,
    },
  });

  const applyToChildren = useWatch({ control, name: "applyToChildren" });

  useEffect(() => {
    if (open) {
      reset({
        newPartialValue: currentPartialValue ?? 0,
        operatorNumber: "",
        applyToChildren: forceApplyToChildren,
      });
    }
  }, [open, reset, forceApplyToChildren]);

  const onSubmit = (data: FormData) => {
    onConfirm(
      Number(data.newPartialValue),
      parseInt(data.operatorNumber, 10),
      data.applyToChildren,
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Set Initial Metric</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Part
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {partName || "-"}
            </Typography>
          </Box>

          {showApplyToChildren ? (
            <Controller
              name="applyToChildren"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={field.value}
                      onChange={field.onChange}
                      disabled={forceApplyToChildren}
                      color="primary"
                    />
                  }
                  label="Apply to all sub-parts"
                />
              )}
            />
          ) : null}

          {applyToChildren ? (
            <Alert severity="warning">
              This will set the initial metric for all installed sub-parts under{" "}
              {partName}. Empty positions will be skipped.
            </Alert>
          ) : null}

          <Controller
            name="newPartialValue"
            control={control}
            rules={integerValidationRules}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                label="New Initial Value"
                variant="outlined"
                type="number"
                size="small"
                fullWidth
                error={!!error}
                helperText={error?.message}
              />
            )}
          />

          <Controller
            name="operatorNumber"
            control={control}
            rules={{
              required: "Operator number is required",
            }}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                label="Operator Number"
                fullWidth
                size="small"
                error={!!error}
                helperText={error?.message || "Enter your Operator Number"}
                inputProps={{
                  inputMode: "numeric",
                }}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, "");
                  field.onChange(value);
                }}
              />
            )}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          variant="contained"
          color="primary"
          disabled={isLoading || !isValid}
          startIcon={isLoading ? <CircularProgress size="1rem" /> : undefined}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};
